import { isEqual } from 'lodash';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CampaignNoiseProcessingResponseDto } from '@shared/services/apiService/apiService';

import {
  CampaignNoiseProcessingDraft,
  CampaignNoiseProcessingValidation,
  cloneCampaignNoiseProcessingDraft,
  mapCampaignNoiseProcessingToDraft,
  validateCampaignNoiseProcessingDraft,
} from '../utils/campaignNoiseProcessing.utils';

type UseCampaignNoiseProcessingControllerOptions = {
  noiseProcessing?: CampaignNoiseProcessingResponseDto | null;
  isReadOnly: boolean;
  enabled?: boolean;
};

type UseCampaignNoiseProcessingControllerResult = {
  draft: CampaignNoiseProcessingDraft | null;
  setDraft: Dispatch<SetStateAction<CampaignNoiseProcessingDraft | null>>;
  markSaved: (draft: CampaignNoiseProcessingDraft) => void;
  resetDraft: () => void;
  isDirty: boolean;
  validationErrors: CampaignNoiseProcessingValidation;
  isReadOnly: boolean;
};

export const useCampaignNoiseProcessingController = ({
  noiseProcessing,
  isReadOnly,
  enabled = true,
}: UseCampaignNoiseProcessingControllerOptions): UseCampaignNoiseProcessingControllerResult => {
  const [localDraft, setLocalDraft] = useState<CampaignNoiseProcessingDraft | null>(null);
  const [serverDraft, setServerDraft] = useState<CampaignNoiseProcessingDraft | null>(null);
  const [initializedFromServer, setInitializedFromServer] = useState(false);
  const pendingSavedDraftRef = useRef<CampaignNoiseProcessingDraft | null>(null);

  const nextMappedDraft = useMemo(
    () => mapCampaignNoiseProcessingToDraft(noiseProcessing),
    [noiseProcessing],
  );

  const isDirty = useMemo(
    () => Boolean(localDraft && serverDraft && !isEqual(localDraft, serverDraft)),
    [localDraft, serverDraft],
  );

  const validationErrors = useMemo(
    () =>
      localDraft
        ? validateCampaignNoiseProcessingDraft(localDraft)
        : { noiseGuard: {}, noiseCancellation: {} },
    [localDraft],
  );

  useEffect(() => {
    if (!enabled) {
      setLocalDraft(null);
      setServerDraft(null);
      setInitializedFromServer(false);
      pendingSavedDraftRef.current = null;
      return;
    }

    const nextDraft = cloneCampaignNoiseProcessingDraft(nextMappedDraft);

    if (pendingSavedDraftRef.current) {
      if (!isEqual(nextDraft, pendingSavedDraftRef.current)) {
        return;
      }

      pendingSavedDraftRef.current = null;
    }

    setServerDraft(nextDraft);

    // Keep user edits when a background refetch returns newer server data.
    if (!initializedFromServer || !isDirty) {
      setLocalDraft(cloneCampaignNoiseProcessingDraft(nextDraft));
      setInitializedFromServer(true);
    }
  }, [enabled, initializedFromServer, isDirty, nextMappedDraft]);

  const markSaved = useCallback((draft: CampaignNoiseProcessingDraft) => {
    const savedDraft = cloneCampaignNoiseProcessingDraft(draft);
    pendingSavedDraftRef.current = savedDraft;
    setServerDraft(savedDraft);
    setLocalDraft((currentDraft) =>
      currentDraft && !isEqual(currentDraft, draft)
        ? currentDraft
        : cloneCampaignNoiseProcessingDraft(savedDraft),
    );
  }, []);

  const resetDraft = useCallback(() => {
    pendingSavedDraftRef.current = null;
    setLocalDraft(serverDraft ? cloneCampaignNoiseProcessingDraft(serverDraft) : null);
  }, [serverDraft]);

  return {
    draft: localDraft,
    setDraft: setLocalDraft,
    markSaved,
    resetDraft,
    isDirty,
    validationErrors,
    isReadOnly,
  };
};
